import { useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { PlusIcon } from '@phosphor-icons/react';
import { useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { Button } from '@/components/ui/button';
import Link from '@/components/ui/Link';
import { AppHeaderConfig } from '@/contexts/AppHeaderContext';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { useSpaceBudget } from '@/hooks/queries/useSpaceBudget';
import { useSpaceCommitments } from '@/hooks/queries/useSpaceCommitments';
import { useSpaceExpensesPage } from '@/hooks/queries/useSpaceExpensesPage';
import { useUtcDay, utcDayDate } from '@/hooks/useUtcDay';
import { hasCommitments } from '@/lib/spaces/commitments';
import { HOME_RECENT_EXPENSES, SPACE_HOME_TITLE_ID, homeDateLine, homeHeroTitle } from '@/lib/spaces/home';
import { SPACE_SHEET_PARAM, spaceSheetFromParams, type SpaceSheet } from '@/lib/spaces/navigation';
import { settlementPeriod } from '@/lib/spaces/settlement';
import type { SpaceDetail } from '@/types/spaces';
import { SpaceBudgetSheet } from './SpaceBudgetSheet';
import { SpaceCardBoundary } from './SpaceCardBoundary';
import { SpaceHomeBalance } from './SpaceHomeBalance';
import { SpaceHomeCategories } from './SpaceHomeCategories';
import { SpaceHomeCommitments } from './SpaceHomeCommitments';
import { SpaceHomeHero } from './SpaceHomeHero';
import { SpaceHomeRecent } from './SpaceHomeRecent';
import { SpaceSettlementSheet } from './SpaceSettlementSheet';

export function SpaceHomePage() {
  const { space } = useSpaceScope();
  const { userId } = useAuth();
  const day = useUtcDay();
  if (!space || !userId) return null;
  // A partial or older space body may lack the roster; the home still renders, with nobody listed.
  const safe = Array.isArray(space.members) ? space : { ...space, members: [] };
  // The home is about now: the current UTC month for a house, the whole trip for a trip. It has no month picker,
  // and it follows the UTC day so a tab left open across midnight moves to the new day, countdown and month.
  const now = utcDayDate(day);
  const period = settlementPeriod(space.kind, null, now);
  // Keyed by period, not by day: a new day re-renders the date line and the cards that count days, while an open sheet
  // (ticked transfers, a budget draft) survives midnight until the month itself turns.
  return <HomeSession key={`${userId}:${space.id}:${period}`} space={safe} userId={userId} now={now} period={period} />;
}

// The address owns which sheet is open, so Back closes it and a link opens it.
function HomeSession({ space, userId, now, period }: Readonly<{ space: SpaceDetail; userId: string; now: Date; period: string }>) {
  const [params, setParams] = useSearchParams();
  const requested = params.get(SPACE_SHEET_PARAM);
  const sheet = spaceSheetFromParams(params, space.model);
  // A sheet this space cannot show (a typo, Acerto in a legacy assigned space) leaves the address too, like a
  // Despesas filter the page cannot honour: the address never claims what the page ignores.
  useEffect(() => {
    if (requested === null || requested === sheet) return;
    const target = new URLSearchParams(params);
    target.delete(SPACE_SHEET_PARAM);
    setParams(target, { replace: true });
  }, [requested, sheet, params, setParams]);
  const openSheet = (next: SpaceSheet) => { const target = new URLSearchParams(params); target.set(SPACE_SHEET_PARAM, next); setParams(target); };
  const closeSheet = () => { const target = new URLSearchParams(params); target.delete(SPACE_SHEET_PARAM); setParams(target, { replace: true }); };
  const openSettle = () => openSheet('settle');
  const openBudget = () => openSheet('budget');
  const budget = useSpaceBudget(space.id, period);
  // Recurring commitments are monthly: a trip has none, so it neither reads them nor shows their card.
  const monthly = hasCommitments(space.kind);
  const commitments = useSpaceCommitments(space.id, monthly ? period : null);
  const recent = useSpaceExpensesPage(space.id, period, 'all', 0, HOME_RECENT_EXPENSES);
  const { mutate } = useSWRConfig();
  const refreshHero = () => Promise.all([budget.mutate(), commitments.mutate()]);
  // The balance card owns its reads (the ledger, or one payer page per member in the assigned model), so its
  // boundary refreshes them by key: clearing the failed flag alone would render the same cached body again.
  const refreshBalance = () => mutate((key) => Array.isArray(key) && key[1] === userId && key[2] === space.id && ['space-ledger', 'space-expenses-page', 'space-commitments'].includes(key[0]));
  const heroTitle = homeHeroTitle(space, Boolean(budget.data?.limitCents), period);
  return <section className="space-y-4 sm:space-y-5">
    <AppHeaderConfig hidePeriodPicker />
    <HomeGreeting space={space} now={now} />
    <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3">
      <div className="min-w-0 lg:col-span-2"><SpaceCardBoundary title={heroTitle} onRetry={refreshHero}><SpaceHomeHero space={space} period={period} budget={budget} commitments={monthly ? commitments : null} onBudget={openBudget} /></SpaceCardBoundary></div>
      <div className="min-w-0"><SpaceCardBoundary title={space.model === 'split' ? 'Acerto' : 'Responsabilidades'} onRetry={refreshBalance}><SpaceHomeBalance space={space} userId={userId} period={period} commitments={monthly ? commitments : null} onSettle={openSettle} /></SpaceCardBoundary></div>
    </div>
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-4"><SpaceCardBoundary title="Últimas despesas" onRetry={recent.mutate}><SpaceHomeRecent space={space} userId={userId} period={period} recent={recent} /></SpaceCardBoundary></div>
      <div className="flex min-w-0 flex-col gap-4">
        <SpaceCardBoundary title="Categorias" onRetry={budget.mutate}><SpaceHomeCategories space={space} budget={budget} onBudget={openBudget} /></SpaceCardBoundary>
        <FixedExpenses visible={monthly} space={space} period={period} commitments={commitments} />
      </div>
    </div>
    <HomeSheet sheet={sheet} space={space} userId={userId} period={period} close={closeSheet} />
  </section>;
}

function HomeGreeting({ space, now }: Readonly<{ space: SpaceDetail; now: Date }>) {
  return <header className="flex items-start justify-between gap-3 sm:items-center">
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
      <h1 id={SPACE_HOME_TITLE_ID} tabIndex={-1} className="min-w-0 break-words text-2xl font-semibold tracking-tight text-text-primary focus-visible:outline-none sm:text-3xl">{space.name}</h1>
      <p className="font-mono text-xs text-text-secondary">{homeDateLine(space, now)}</p>
    </div>
    <SendExpense space={space} />
  </header>;
}

// Sending starts from a personal transaction, so this leaves the space scope on purpose.
function SendExpense({ space }: Readonly<{ space: SpaceDetail }>) {
  if (space.myRole !== 'editor' || space.archivedAt) return null;
  return <Button asChild size="sm" variant="outline" className="shrink-0 max-sm:size-9 max-sm:px-0">
    <Link href="/transactions" aria-label="Enviar despesa"><PlusIcon aria-hidden weight="bold" className="size-4" /><span className="max-sm:sr-only">Enviar despesa</span></Link>
  </Button>;
}

function FixedExpenses({ visible, space, period, commitments }: Readonly<{ visible: boolean; space: SpaceDetail; period: string; commitments: ReturnType<typeof useSpaceCommitments> }>) {
  if (!visible) return null;
  return <SpaceCardBoundary title="Despesas fixas" onRetry={commitments.mutate}><SpaceHomeCommitments space={space} period={period} commitments={commitments} /></SpaceCardBoundary>;
}

function HomeSheet({ sheet, space, userId, period, close }: Readonly<{ sheet: SpaceSheet | null; space: SpaceDetail; userId: string; period: string; close: () => void }>) {
  if (sheet === 'settle') return <SpaceSettlementSheet space={space} userId={userId} close={close} />;
  if (sheet === 'budget') return <SpaceBudgetSheet space={space} period={period} close={close} />;
  return null;
}
